import { z } from 'zod';
import { REGISTRATION_CONSENT_TYPES } from '../members/consent-documents';
import { assessmentQuestionIdSchema } from '../api/assessments';
import { checkInDateSchema, getCheckInsParamsSchema } from '../check-ins/schema';
import { searchHelpParamsSchema } from '../domain-docs/tool-schema';
import { jsonValueSchema, nonEmptyJsonPointerSchema } from './json-pointer';
import { optInKindSchema } from '../opt-in/schema';
import { reminderTimeSchema } from '../reminders/schema';
import { showOptionsParametersSchema } from './show-options';
import { weeklyPlanSelectionSchema } from '../weekly-plan/schema';
import { isUnknownRecord as isObjectRecord } from '../validation/unknown-record';

export { isJsonPointer } from './json-pointer';
export type { JsonValue } from './json-pointer';

export const MAX_TOOL_CHAIN_DEPTH = 8;

export type ToolChainInspection =
  | { ok: true; depth: number }
  | { ok: false; reason: 'cycle' | 'max_depth'; depth?: number };

export function inspectToolChainDepth(
  tools: unknown,
  maxDepth = MAX_TOOL_CHAIN_DEPTH,
): ToolChainInspection {
  if (!Array.isArray(tools)) return { ok: true, depth: 0 };

  const active = new WeakSet<object>();
  let deepest = 0;

  function visitChain(chain: unknown, depth: number): ToolChainInspection {
    if (!Array.isArray(chain)) return { ok: true, depth: deepest };
    if (depth > maxDepth) return { ok: false, reason: 'max_depth', depth };
    if (active.has(chain)) return { ok: false, reason: 'cycle' };

    active.add(chain);
    deepest = Math.max(deepest, depth);

    for (const call of chain) {
      if (!isObjectRecord(call)) continue;
      if (active.has(call)) return { ok: false, reason: 'cycle' };

      active.add(call);
      for (const field of ['onSuccess', 'onError'] as const) {
        const branch = call[field];
        if (Array.isArray(branch)) {
          const result = visitChain(branch, depth + 1);
          if (!result.ok) return result;
        }
      }
      active.delete(call);
    }

    active.delete(chain);
    return { ok: true, depth: deepest };
  }

  return visitChain(tools, 1);
}

/**
 * Host-owned flow tools carry no document-authored parameters. The validation
 * gate and double-tap guard live in the host binding, not the document. The
 * explicit `Record<string, never>` typing keeps the derived per-tool parameter
 * types strict (an empty-shape ZodObject would infer `{}`, which accepts any
 * object at the type level).
 */
const hostToolParams = z.record(z.string(), z.never());

/**
 * A parameter a document may author either as a literal or as a `{ $state }`
 * read the renderer resolves at dispatch.
 *
 * One schema validates two different moments — the authored document (where the
 * value is still an expression) and the dispatched call (where it is the
 * resolved literal) — so it has to admit both. `clear_state_unless_included`
 * hits the same problem and solves it by widening to `jsonValueSchema`; naming
 * the union instead keeps the literal's own constraints enforced, so a
 * statically-authored bad value is still caught at author time.
 */
function stateReadableParam<T extends z.ZodTypeAny>(literal: T) {
  return z.union([literal, z.object({ $state: nonEmptyJsonPointerSchema }).strict()]);
}

/**
 * "Open the member's check-in history", optionally landing on a specific day.
 *
 * The day is optional because two entry points share this one tool: the home ⚡
 * streak pill opens the history with nothing selected but today, while a home
 * week-strip day chip opens it on the day the member tapped. Forking that into
 * two tools would split one surface's semantics on a parameter, so the parameter
 * carries the difference instead. `.default({})` keeps a `parameters`-less call
 * parsing, which is what the pill authors.
 *
 * The strip authors its day as a `{ $state }` read of its own bound selection,
 * so the parameter admits that expression alongside the resolved date.
 */
const openCheckInsParams = z
  .object({ date: stateReadableParam(checkInDateSchema).optional() })
  .strict()
  .default({});

const startIntakeConversationParams = z.object({ mode: z.enum(['talk', 'text']) }).strict();

/** A host-owned state write addressed by JSON Pointer. */
const stateWriteParams = z
  .object({
    path: nonEmptyJsonPointerSchema,
    value: jsonValueSchema,
  })
  .strict();

/**
 * A host-owned membership toggle: adds `value` to the array at `path` when it is
 * absent, removes it when present.
 *
 * The complement of `set_state` for a card list bound to one array path. A
 * document can say "write this exact value" but not "write the array I already
 * have, plus or minus this element", so a multi-select built from individually
 * pressable cards needs the host to compute the next array.
 *
 * Deliberately carries NO current value, unlike `clear_state_unless_included`
 * below: the host reads and writes the same store synchronously inside one
 * handler, so successive taps each see the previous one's result. Passing a
 * pre-resolved `{ $state }` array here would reintroduce exactly the staleness
 * it is meant to avoid — two taps landing in one React commit would both
 * compute from the same snapshot and the first would be lost.
 */
const stateMembershipToggleParams = z
  .object({
    path: nonEmptyJsonPointerSchema,
    value: z.string().min(1),
  })
  .strict();

/**
 * A host-owned conditional reset: clears `targetPath` unless `currentValue`
 * (an array) still includes `value`. Pairs with `@json-render/core`'s patched
 * `includes` visibility comparator to fully support a MultiSelect-driven
 * nested reveal — the comparator hides the reveal, this clears its own answer.
 *
 * `currentValue` is authored as a `{ $state }` binding (not a path string the
 * host re-derives): a `watch`-triggered tool call is resolved against the
 * renderer's own live internal store at dispatch time, which can be one React
 * commit ahead of the separate host-level `FlowStateAdapter` (synced back via
 * a slower onStateChange -> mergeAnswers -> re-render round trip). Taking the
 * already-resolved value sidesteps that staleness window entirely.
 */
const clearStateUnlessIncludedParams = z
  .object({
    currentValue: jsonValueSchema,
    value: jsonValueSchema,
    targetPath: nonEmptyJsonPointerSchema,
  })
  .strict();

/** A host-owned opt-in toggle addressed by the AgentScreen element's state path. */
const toggleOptInParams = z
  .object({
    statePath: nonEmptyJsonPointerSchema,
  })
  .strict();

const calculateWeekSummaryParams = z
  .object({
    sourcePaths: z.array(nonEmptyJsonPointerSchema).length(7),
    targetPath: nonEmptyJsonPointerSchema,
    activeColor: z.string().min(1),
    freeColor: z.string().min(1),
    totalLabel: z.string().min(1),
    freeLabel: z.string().min(1),
    dayLabels: z.array(z.string().min(1)).length(7),
    dayAccessibilitySuffix: z.string().min(1),
    /** Singular form of `dayAccessibilitySuffix`, used for a day whose count is exactly 1. */
    dayAccessibilitySuffixOne: z.string().min(1),
    /**
     * Declared write target for the device-local calendar date anchoring the
     * rolling day window (the summary handler persists it alongside the day
     * values, so downstream weekday labelling never guesses from a UTC
     * timestamp). Authored statically in the flow config.
     */
    anchorPath: nonEmptyJsonPointerSchema.optional(),
    /**
     * The anchor value (`YYYY-MM-DD`, device-local "today") — injected at
     * render by the weekly-day dating transform so the labels the member sees
     * and the persisted anchor come from one clock.
     */
    anchorDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .optional(),
  })
  .strict();

/**
 * Host-owned derived-label rows request: map option-value answers at
 * `sourcePath` to display labels via the authored `labels` map, writing the
 * resolved string (or null when absent/unmappable) to `targetPath`. Array
 * answers join their labels with `joinWith`.
 */
const deriveContextRowContract = z
  .object({
    sourcePath: nonEmptyJsonPointerSchema,
    targetPath: nonEmptyJsonPointerSchema,
    labels: z.record(z.string(), z.string().min(1)),
    joinWith: z.string().optional(),
  })
  .strict();
const deriveContextRowsParams = z
  .object({
    rows: z.array(deriveContextRowContract).min(1),
  })
  .strict();

/**
 * Host-owned personalised-bullet request: build a summary bullet that echoes the
 * member's own selections. Each `sources` entry maps the option-value answer(s)
 * at its `sourcePath` to short authored display phrases via `labels`; every
 * resolved phrase across the sources (in order) is joined with `joinWith` and
 * substituted into the `{values}` slot of `template`, and the result is written
 * to `targetPath`. A required non-empty `fallback` (used when no phrase resolves)
 * guarantees the bullet can never render blank. Array answers flatten; a present
 * value with no authored phrase is skipped with a path-only warning.
 */
const deriveSummaryBulletSourceContract = z
  .object({
    sourcePath: nonEmptyJsonPointerSchema,
    labels: z.record(z.string(), z.string().min(1)),
  })
  .strict();
const deriveSummaryBulletContract = z
  .object({
    targetPath: nonEmptyJsonPointerSchema,
    template: z
      .string()
      .min(1)
      .refine((t) => t.includes('{values}'), {
        message: 'template must contain the {values} slot',
      }),
    joinWith: z.string(),
    fallback: z.string().min(1),
    sources: z.array(deriveSummaryBulletSourceContract).min(1),
  })
  .strict();
const deriveSummaryBulletsParams = z
  .object({
    bullets: z.array(deriveSummaryBulletContract).min(1),
  })
  .strict();

/** Host-owned persisted flow session save request. */
const saveFlowSessionParams = z
  .object({
    finalize: z.boolean().optional().default(false),
  })
  .strict();

/** Host-owned feedback completion request. */
const submitFeedbackParams = z
  .object({
    intent: z.enum(['submit', 'skip']).optional().default('submit'),
    ratingPath: nonEmptyJsonPointerSchema.optional(),
    notesPath: nonEmptyJsonPointerSchema.optional(),
    responsesPath: nonEmptyJsonPointerSchema.optional(),
  })
  .strict();

/** Insight card payload produced by a provider tool call. */
const logInsightParams = z
  .object({
    title: z.string().min(1),
    description: z.string().min(1),
  })
  .strict();

/**
 * Progress-chart card payload produced by a provider tool call.
 *
 * `daily_series` is a comma-separated string, not a numeric array, because the
 * model transcribes it verbatim from a single authored line — copying a
 * substring is markedly more reliable than restructuring it into typed values.
 * This is the wire contract only: it constrains what the provider may send.
 * The semantic check (exactly one integer per day of the period, none
 * negative) belongs to the consumer that parses the string, so a malformed
 * series is caught with the full context needed to fall back gracefully rather
 * than rejected here with none.
 */
const showProgressChartParams = z
  .object({
    daily_series: z.string().min(1),
    baseline_average: z.number().nonnegative(),
    period_days: z.number().int().positive(),
    current_target: z.number().nonnegative(),
  })
  .strict();

/** Host-driven navigation target for the GenUI flow shell. */
const navigateToScreenParams = z
  .object({
    screenId: z.enum(['insight', 'help', 'crisis']),
  })
  .strict();

/**
 * Host-owned semantic navigation to the in-app chat. The optional
 * `agentDefinitionId` is an opaque purpose handle (never a raw provider agent
 * id); the host maps it to the chat route (`chatLaunchHref` → `router.push`), so
 * no route/URL is authored in the document (ADR-046 Rule 7).
 */
const openChatParams = z
  .object({
    agentDefinitionId: z.string().optional(),
  })
  .strict();

/**
 * Host-owned navigation from a journey practice tile into the conversation
 * surface. `composedPracticeId` is the opaque id of the member's composed
 * practice — never practice content, technique identity, or a provider id: the
 * server resolves the row and bakes the practice into the session's prompt, so
 * the id is all that transits the client (ADR-046 Rule 7). Required, unlike
 * `open_chat`'s optional handle — a practice launch without its practice is
 * meaningless.
 */
const openPracticeParams = z
  .object({
    composedPracticeId: z.string().uuid(),
  })
  .strict();

/**
 * Host-owned navigation into a CBT module's screen. `moduleKey` is the module's
 * catalogued key: the screen fetches the module's served content itself, so the
 * key is all that transits the client and the document names no route
 * (ADR-046 Rule 7). Required, like `open_practice`'s id: a module launch without
 * its module is meaningless.
 */
const openCbtModuleParams = z
  .object({
    moduleKey: z.string().min(1),
  })
  .strict();

/**
 * Host-owned entry straight into a member's care call. `appointmentId` is the
 * opaque id of the booking the host resolves to a call route; no member
 * identity travels in the parameters — the host resolves that from the
 * authenticated session (ADR-044 Rule 4). Distinct from `open_care_tab`, which
 * only lands the member on the care surface and leaves the call one tap away.
 */
const joinCareCallParams = z
  .object({
    appointmentId: z.string().uuid(),
  })
  .strict();

/**
 * Host-owned "open the care-team booking sheet" action for the home "Book a
 * call" card. `phase` picks where the demo booking sheet opens: `confirm` (the
 * preselected slot, from "Book this slot") or `select` (the slot list, from
 * "Show more options"). Host-internal (never advertised to the voice provider);
 * the mobile host owns the sheet.
 */
const openCareBookingParams = z
  .object({
    phase: z.enum(['select', 'confirm']),
  })
  .strict();

/**
 * The care-team booking card's host-owned step actions, driven by the member
 * tapping the card controls: `advance` (select → confirm, guarded on a full
 * selection), `change` (confirm → select), `confirm` (confirm → booked +
 * resolve), `cancel` (dismiss + resolve). One parameterized host verb rather
 * than four tools, mirroring `open_care_booking(phase)`.
 */
export const careBookingStepActionSchema = z.enum(['advance', 'change', 'confirm', 'cancel']);
export type CareBookingStepAction = z.infer<typeof careBookingStepActionSchema>;
const careBookingStepParams = z.object({ action: careBookingStepActionSchema }).strict();

/** Legacy provider capture shape. Its numeric target does not establish clinical quantity intent. */
const setWeeklyPlanParams = z
  .object({
    title: z.string().trim().min(1).max(160),
    description: z.string().trim().min(1).max(1000),
    target_value: z.number(),
  })
  .strict();

/**
 * Starts the host-owned care-team booking interaction. The member selects and
 * confirms the appointment in the client, so this carries only the referral
 * reason, never member identity or appointment data.
 */
const bookCareTeamCallParams = z
  .object({
    referral_reason: z.string().min(1),
  })
  .strict();

/**
 * Ask the server to build a practice from a named member intent. `intent` is
 * the one member-shaped sentence the builder matches against the signed
 * technique catalogue; `anchor` optionally carries the member's own concrete
 * specifics when they have already surfaced in the conversation. Identity is
 * never an argument (ADR-045): the server resolves the member from the call's
 * session binding, and the system-provided conversation correlation travels in
 * the provider request envelope, not in this LLM-authored schema.
 */
const buildPracticeParams = z
  .object({
    intent: z.string().min(1),
    anchor: z.string().min(1).optional(),
  })
  .strict();

/**
 * Active-crisis escalation (suicidal ideation, self-harm, intent to harm).
 * Carries only a brief factual reason — the client surfaces crisis support and
 * the hotline; identity is never an argument.
 */
const escalateCrisisParams = z
  .object({
    reason: z.string().min(1),
  })
  .strict();

/** Save the current answer value found at a host-owned state pointer. */
const saveAssessmentAnswerParams = z
  .object({
    question_id: assessmentQuestionIdSchema,
    answer_path: nonEmptyJsonPointerSchema,
  })
  .strict();

/**
 * A generic member opt-in request. `kind` (a closed catalog) selects the
 * capability the app runs when the member accepts; `title`/`description` are the
 * member-facing card copy; `detail` is an optional secondary line (e.g. a
 * suggested time label). No identity field — the member acts on the card and
 * nothing is resolved from arguments (ADR-044 Rule 4).
 */
const requestOptInParams = z
  .object({
    kind: z.literal(optInKindSchema.enum.daily_checkin_reminder),
    title: z.string().min(1),
    description: z.string().min(1),
    detail: z.string().optional(),
    settings: z.object({ reminderTime: reminderTimeSchema }).strict(),
  })
  .strict();

/**
 * A member's binary willingness to connect with the care team, captured at the
 * close of a practice conversation. `interested` is the whole signal; `reason`
 * is an optional free-text note the member may give (treated as potential PHI —
 * persisted server-side but never published in the domain event or analytics).
 * No identity field — the member is resolved server-side from the call.
 */
const recordCareTeamInterestParams = z
  .object({
    interested: z.boolean(),
    reason: z.string().optional(),
  })
  .strict();

/**
 * The member's kept weekly-plan topics, as ids only.
 *
 * Ids and nothing else: the host cannot send replacement `title`/`blurb` copy
 * even by accident, because there is nowhere to put it. The server owns the
 * words the member reads, and the save route re-reads them from the stored set
 * rather than trusting anything that arrives here (ADR-046 Rule 7 — the document
 * declares intent, the host and server own the effect).
 *
 * `selected_suggestion_ids` accepts either the resolved ids OR a `{ $state }`
 * read expression, because the tool chain is validated STATICALLY at the catalog
 * boundary while the renderer resolves bindings only at dispatch. A document
 * authoring the member's live selection therefore cannot present a literal array
 * to the validator, and a schema that demanded one would reject every real
 * authoring of this tool. The same reason `clear_state_unless_included` types its
 * `currentValue` permissively.
 *
 * The bound is not lost, only moved: the host handler parses the RESOLVED value
 * with `weeklyPlanSelectionSchema` before it makes a request, so an out-of-range
 * or duplicated selection still fails closed at the dispatcher boundary rather
 * than reaching the network.
 */
const saveWeeklyPlanTopicsParams = z
  .object({
    suggestion_set_id: z.string().min(1),
    selected_suggestion_ids: z.union([
      weeklyPlanSelectionSchema,
      z.object({ $state: nonEmptyJsonPointerSchema }).strict(),
    ]),
  })
  .strict();

/** Whether a dispatcher waits for a tool and how failures affect the chain. */
export type ToolRunMode = 'immediate' | 'blocking' | 'interactive';

/** Where the tool is implemented after a provider or renderer requests it. */
export type ToolImplementation = 'host' | 'client' | 'server';

/** Provider registration shape for Galapagos tools exposed through a voice edge. */
export type ToolProviderType = 'client' | 'webhook';

export interface ToolDefinition {
  parametersSchema: z.ZodTypeAny;
  runMode: ToolRunMode;
  implementation: ToolImplementation;
  /**
   * How the tool registers with the voice provider — set only on tools we
   * deliberately expose outside the app. Host-implemented tools are internal to
   * the GenUI host (driven by documents and the flow shell): their names and
   * parameter shapes (e.g. `set_state`'s JSON Pointer paths) stay private to the
   * host, so they omit this and are never advertised to the provider.
   */
  providerType?: ToolProviderType;
}

export type ToolCatalog = Record<string, ToolDefinition>;

/**
 * The shared executable tool catalog. Provider-specific metadata stays at the edge, but
 * every Galapagos tool uses the same executable call envelope:
 * `{ tool_name, parameters }`.
 */
export const TOOL_CATALOG = {
  save_app_onboarding_step: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  advance_flow: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  go_back: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  set_state: {
    parametersSchema: stateWriteParams,
    runMode: 'immediate',
    implementation: 'host',
  },
  toggle_state_membership: {
    parametersSchema: stateMembershipToggleParams,
    runMode: 'immediate',
    implementation: 'host',
  },
  clear_state_unless_included: {
    parametersSchema: clearStateUnlessIncludedParams,
    runMode: 'immediate',
    implementation: 'host',
  },
  complete_intake_plan: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  open_checkin_reminder_picker: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'immediate',
    implementation: 'host',
  },
  toggle_checkin_reminder: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'immediate',
    implementation: 'host',
  },
  calculate_week_summary: {
    parametersSchema: calculateWeekSummaryParams,
    // The next screen reads this result. Invalid saved choices must stop the
    // chain before advance_flow, just like other state-producing tools (ADR-046).
    runMode: 'blocking',
    implementation: 'host',
  },
  derive_context_rows: {
    parametersSchema: deriveContextRowsParams,
    // Blocking, not immediate: the next screen (`confirm_context`) binds to the
    // state this writes, and it is chained before `advance_flow` — a result that
    // controls later behaviour must be awaited (ADR-046 Rule 14).
    runMode: 'blocking',
    implementation: 'host',
  },
  derive_summary_bullets: {
    parametersSchema: deriveSummaryBulletsParams,
    // Blocking: the summary screen binds to the state this writes, and it is
    // chained before `advance_flow` into that screen (same contract as
    // derive_context_rows — ADR-046 Rule 14).
    runMode: 'blocking',
    implementation: 'host',
  },
  save_flow_session: {
    parametersSchema: saveFlowSessionParams.prefault({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  submit_feedback: {
    parametersSchema: submitFeedbackParams.prefault({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  // The talk-or-text choice before the first Sona conversation. The document
  // names intents only: the host owns the OS permission dialog, the onboarding
  // finalize write, the post-finalize routing, and the settings deep link
  // (ADR-046 Rules 7, 17).
  //
  // Blocking: its outcome decides whether the chain continues into the start or
  // into the denied branch, so the dispatcher must await it (ADR-046 Rule 14).
  // A denial fails the call so the document's `onError` runs.
  request_microphone_permission: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  start_intake_conversation: {
    parametersSchema: startIntakeConversationParams,
    runMode: 'blocking',
    implementation: 'host',
  },
  // Finalize onboarding with no conversation and take the manual path: the
  // microphone sheet's way forward for a member who refused the microphone,
  // and the terminal Continue of a flow served to an AI-restricted member.
  // Blocking for the same reason as the start: the chain's success branch may
  // continue only after the finalize landed.
  continue_without_sona: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  open_app_settings: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'immediate',
    implementation: 'host',
  },
  show_progress_chart: {
    parametersSchema: showProgressChartParams,
    runMode: 'immediate',
    implementation: 'client',
    providerType: 'client',
  },
  show_options: {
    parametersSchema: showOptionsParametersSchema,
    runMode: 'interactive',
    implementation: 'client',
    providerType: 'client',
  },
  log_insight: {
    parametersSchema: logInsightParams,
    runMode: 'immediate',
    implementation: 'client',
    providerType: 'client',
  },
  navigate_to_screen: {
    parametersSchema: navigateToScreenParams,
    runMode: 'immediate',
    implementation: 'host',
  },
  open_chat: {
    parametersSchema: openChatParams.default({}),
    runMode: 'immediate',
    implementation: 'host',
  },
  // No `.default({})` — the practice id is required, so a parameterless call
  // must fail at the dispatcher boundary rather than launch nothing.
  open_practice: {
    parametersSchema: openPracticeParams,
    runMode: 'immediate',
    implementation: 'host',
  },
  open_care_booking: {
    parametersSchema: openCareBookingParams,
    runMode: 'immediate',
    implementation: 'host',
  },
  open_checkin: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'immediate',
    implementation: 'host',
  },
  // Host-owned entry into the guided-conversations introduction for the
  // welcome series. Parameterless: the screens read the member's own sessions,
  // so the document names no practice, route or series (ADR-046 Rule 7).
  open_guided_conversations: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'immediate',
    implementation: 'host',
  },
  // Host-owned "open the member's rewards" navigation. Host-internal (never
  // advertised to the voice provider); the host maps it to the rewards
  // destination, so no route/URL is authored in the document (ADR-046 Rule 7).
  open_rewards: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'immediate',
    implementation: 'host',
  },
  // Host-owned "open the member's check-in history" surface — the scrollable
  // month calendar of past check-ins. Host-internal (never advertised to the
  // voice provider); the host owns which surface renders it, so no route/URL is
  // authored in the document (ADR-046 Rule 7). The optional `date` narrows which
  // day the surface lands on; the host decides what an absent one means.
  open_check_ins: {
    parametersSchema: openCheckInsParams,
    runMode: 'immediate',
    implementation: 'host',
  },
  // Host-owned entry into the member's first weekly-plan review. The document
  // carries only the intent: session start/resume, cache seeding, deduplication,
  // and guarded navigation all remain host concerns (ADR-046 Rule 7).
  open_weekly_plan_review: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'immediate',
    implementation: 'host',
  },
  // Host-owned entry into the shared Sona AI consent screen from a surface a
  // member reaches after onboarding (the Sona-off plan card). The document
  // carries only the intent; the host owns the route and the return
  // (ADR-046 Rule 7).
  open_ai_consent_gate: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'immediate',
    implementation: 'host',
  },
  // Host-owned "open the BAM check-in questionnaire" navigation, dispatched by
  // the server-gated home entry card. Host-internal (never advertised to the
  // voice provider); the host maps it to the questionnaire flow's entry step, so
  // no route/URL is authored in the document (ADR-046 Rule 7).
  open_bam: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'immediate',
    implementation: 'host',
  },
  // Host-owned "open the care-team introduction" navigation, dispatched by the
  // unassigned member's home entry card and the generic care-tab card. The
  // host maps it to the care-team introduction screen, so no route/URL is
  // authored in the document (ADR-046 Rule 7).
  open_care_team_introduction: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'immediate',
    implementation: 'host',
  },
  // Host-owned "open the care tab" navigation, dispatched by the home surface's
  // booked-appointment card. The card is a pointer to the member's care-team
  // surface (where the call, its Join button, and its details all live), not a
  // deep link into one call, so it carries no appointment id and the host owns
  // the tab route (ADR-046 Rule 7).
  open_care_tab: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'immediate',
    implementation: 'host',
  },
  // Host-owned "take the member into the care call itself", dispatched by the
  // home surface once a booked call has started. `open_care_tab` lands on the
  // care surface and leaves the call one tap away, so a control that means
  // "your call has started" needs a tool carrying the appointment id and
  // opening the call; the host still owns the route (ADR-046 Rule 7).
  join_care_call: {
    parametersSchema: joinCareCallParams,
    runMode: 'immediate',
    implementation: 'host',
  },
  // Host-owned "open one booked call's manage page", dispatched by the leading
  // appointment card's edit control. Reschedule and cancel live on that surface;
  // the document names only the tool and the appointment id (ADR-046 Rule 7).
  open_care_call_details: {
    parametersSchema: joinCareCallParams,
    runMode: 'immediate',
    implementation: 'host',
  },
  // Host-owned "open the member's upcoming calls" navigation, dispatched by the
  // home surface's View all link when more than one call is booked. The host
  // maps it to the screen that lists and manages every upcoming call (ADR-046
  // Rule 7); the document names only the tool.
  open_care_upcoming_calls: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'immediate',
    implementation: 'host',
  },
  // Host-owned "open the care-team chat" navigation, dispatched by the home
  // surface's unread-message card CTA. The host maps it to the registered
  // care-team-chat screen (ADR-046 Rule 7). It carries no conversation id:
  // the chat screen resolves the member's own thread itself, so the document
  // never transports a conversation identifier.
  open_care_team_chat: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'immediate',
    implementation: 'host',
  },
  // Host-owned "open the quit date picker" navigation. Parameterless: the
  // picker reads the member's quit date state itself and picks set or change
  // mode from it, so the document names no date, mode or route (ADR-046 Rule 7).
  open_quit_date_picker: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'immediate',
    implementation: 'host',
  },
  // Host-owned "open a CBT module" navigation, dispatched by the Home Essentials
  // card and its Next up row. The host maps it to the module screen.
  open_cbt_module: {
    parametersSchema: openCbtModuleParams,
    runMode: 'immediate',
    implementation: 'host',
  },
  complete_agent_session: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  toggle_opt_in: {
    parametersSchema: toggleOptInParams,
    runMode: 'blocking',
    implementation: 'host',
  },
  suggest_end_call: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'immediate',
    implementation: 'client',
    providerType: 'client',
  },
  set_weekly_plan: {
    parametersSchema: setWeeklyPlanParams,
    runMode: 'immediate',
    implementation: 'client',
    providerType: 'client',
  },
  // Host-owned persistence of the member's chosen weekly-plan topics. Blocking,
  // not immediate: its result decides whether the host navigates back to
  // Settings, and a tool whose result controls later behaviour must be awaited
  // (ADR-046 Rule 14). Host-internal — no `providerType`, so it is never
  // advertised to a voice provider; an agent must not be able to write a
  // member's weekly plan.
  save_weekly_plan_topics: {
    parametersSchema: saveWeeklyPlanTopicsParams,
    runMode: 'blocking',
    implementation: 'host',
  },
  // Review-owned commitment of the selected candidates. It deliberately shares
  // the bounded id-only parameter shape with candidate save, while remaining a
  // distinct blocking intent: the host routes it to the atomic review rollover
  // command rather than the candidate-only Settings endpoint.
  complete_plan_review: {
    parametersSchema: saveWeeklyPlanTopicsParams,
    runMode: 'blocking',
    implementation: 'host',
  },
  // Blocking host-owned persistence for the document-authored initial-review
  // repair Save control. The document carries only the semantic intent; the
  // host owns current quantities, idempotency, pending state, and API execution.
  save_initial_review_check_ins: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  // Blocking host-owned continuation for the migration journey's last content
  // step. The host checks which personalization answers the member still needs
  // to give, otherwise settles the continuity summary, and owns the route that
  // follows either way. The document carries only the intent; it is awaited
  // because the next screen must never mount before that outcome is known, and
  // its `onError` branch is how the step recovers when the read fails.
  continue_migration_review: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  // Immutable displayed-snapshot identity, never transport or persistence policy.
  capture_migration_registration_consent: {
    parametersSchema: z.object({ snapshot: z.string().min(1) }).strict(),
    runMode: 'blocking',
    implementation: 'host',
  },
  open_migration_registration_document: {
    parametersSchema: z
      .object({ snapshot: z.string().min(1), document: z.enum(REGISTRATION_CONSENT_TYPES) })
      .strict(),
    runMode: 'blocking',
    implementation: 'host',
  },
  retry_migration_registration_consent: {
    parametersSchema: z.object({ upload: z.boolean().optional() }).strict().default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  sign_out_migration_registration_consent: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  // Explicit member agreement; the host owns receipt initiation and the
  // session flag. The flow advances only after that capability is accepted.
  record_migration_ai_consent: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  // Retry the currently failing preparation gate. No route, provider or
  // persistence policy is supplied by the document.
  retry_migration_preparation: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  // Await the authenticated lookup and return shown/not_found/session_expired.
  // Immediate dispatch would resolve before the read and discard its result;
  // unlike interactive booking, this does not wait for member interaction.
  show_upcoming_appointment: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'client',
    providerType: 'client',
  },
  book_care_team_call: {
    parametersSchema: bookCareTeamCallParams,
    // The host keeps the invocation pending while the member completes the
    // booking card, then returns one closed outcome to the agent.
    runMode: 'interactive',
    implementation: 'client',
    providerType: 'client',
  },
  // Host-owned step verb for the `book_care_team_call` booking card. The member
  // drives it by tapping the card's controls (not the agent); the `action` param
  // advances select → confirm → booked or dismisses, and the mobile host
  // resolves the booking.
  care_booking_step: {
    parametersSchema: careBookingStepParams,
    runMode: 'blocking',
    implementation: 'host',
  },
  select_show_option: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  build_practice: {
    parametersSchema: buildPracticeParams,
    // Awaited server round-trip: the agent waits for the built practice (or
    // the refusal arm) before speaking again. First webhook-registered tool —
    // the provider resolves it by tool_id only; it never enters the per-call
    // client dispatch set.
    runMode: 'blocking',
    implementation: 'server',
    providerType: 'webhook',
  },
  search_help: {
    parametersSchema: searchHelpParamsSchema,
    // Awaited server round-trip: the agent waits for the matching help
    // sections before answering from them. Reads no member data; the session
    // binding only authorizes the call (ADR-045). Never enters the per-call
    // client dispatch set.
    runMode: 'blocking',
    implementation: 'server',
    providerType: 'webhook',
  },
  get_check_ins: {
    parametersSchema: getCheckInsParamsSchema,
    // Awaited server round-trip: the agent waits for the member's check-in
    // history before reading it back. Member identity is resolved from the
    // call's session binding — no identity field in the schema (ADR-045).
    // Never enters the per-call client dispatch set.
    runMode: 'blocking',
    implementation: 'server',
    providerType: 'webhook',
  },
  escalate_crisis: {
    parametersSchema: escalateCrisisParams,
    runMode: 'immediate',
    implementation: 'client',
    providerType: 'client',
  },
  start_assessment: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  save_assessment_answer: {
    parametersSchema: saveAssessmentAnswerParams,
    runMode: 'blocking',
    implementation: 'host',
  },
  evaluate_assessment: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  complete_assessment: {
    parametersSchema: hostToolParams.default({}),
    runMode: 'blocking',
    implementation: 'host',
  },
  request_opt_in: {
    parametersSchema: requestOptInParams,
    runMode: 'immediate',
    implementation: 'client',
    providerType: 'client',
  },
  record_care_team_interest: {
    parametersSchema: recordCareTeamInterestParams,
    runMode: 'immediate',
    implementation: 'client',
    providerType: 'client',
  },
} as const satisfies ToolCatalog;

export type ToolName = keyof typeof TOOL_CATALOG;

export const TOOL_NAMES = Object.keys(TOOL_CATALOG).filter(isToolName);
const toolNameSchema = z.custom<ToolName>(
  (value) => typeof value === 'string' && isToolName(value),
);

export const TOOL_RUN_MODES: Record<ToolName, ToolRunMode> = z
  .record(toolNameSchema, z.enum(['immediate', 'blocking', 'interactive']))
  .parse(Object.fromEntries(TOOL_NAMES.map((name) => [name, TOOL_CATALOG[name].runMode])));

export const TOOL_IMPLEMENTATIONS: Record<ToolName, ToolImplementation> = z
  .record(toolNameSchema, z.enum(['host', 'client', 'server']))
  .parse(Object.fromEntries(TOOL_NAMES.map((name) => [name, TOOL_CATALOG[name].implementation])));

// A widened view of the catalog so optional provider metadata can be read from
// every tool while the concrete catalog remains the single source of truth.
const TOOL_DEFINITIONS: Record<ToolName, ToolDefinition> = z
  .record(
    toolNameSchema,
    z.object({
      parametersSchema: z.custom<z.ZodType>((value) => value instanceof z.ZodType),
      runMode: z.enum(['immediate', 'blocking', 'interactive']),
      implementation: z.enum(['host', 'client', 'server']),
      providerType: z.enum(['client', 'webhook']).optional(),
    }),
  )
  .parse(TOOL_CATALOG);

/**
 * The tools deliberately exposed to the voice provider — everything that is not
 * host-internal. Adding a tool here is a conscious decision to let the provider
 * call it; host primitives stay off this list.
 */
export const PROVIDER_TOOL_NAMES = TOOL_NAMES.filter(
  (name) => TOOL_DEFINITIONS[name].providerType !== undefined,
);

export const TOOL_PROVIDER_TYPES: Partial<Record<ToolName, ToolProviderType>> = z
  .record(toolNameSchema, z.enum(['client', 'webhook']).optional())
  .parse(
    Object.fromEntries(
      PROVIDER_TOOL_NAMES.map((name) => [name, TOOL_DEFINITIONS[name].providerType]),
    ),
  );

type ToolParamsSchema<K extends ToolName> = (typeof TOOL_CATALOG)[K]['parametersSchema'];

/**
 * Parsed (dispatch-side) parameters for a catalog tool — the `z.output` of its
 * catalog `parametersSchema`. Handlers receive this shape after the tool-call
 * schema has validated (and defaulted) the document-authored value.
 */
export type ToolParameters<K extends ToolName = ToolName> = z.output<ToolParamsSchema<K>>;

type ToolParametersInput<K extends ToolName> = z.input<ToolParamsSchema<K>>;

type ToolBranchInput = {
  onSuccess?: ToolChain;
  onError?: ToolChain;
};

type ParsedToolBranch = {
  onSuccess?: ParsedToolChain;
  onError?: ParsedToolChain;
};

/**
 * The normalized product tool-call shape, derived one arm per catalog entry.
 * Tools whose catalog `parametersSchema` carries a default may omit
 * `parameters`; the schema fills them before dispatch.
 */
type ToolCallFor<K extends ToolName> = (undefined extends ToolParametersInput<K>
  ? { tool_name: K; parameters?: ToolParametersInput<K> }
  : { tool_name: K; parameters: ToolParametersInput<K> }) &
  ToolBranchInput;

export type ToolCall = { [K in ToolName]: ToolCallFor<K> }[ToolName];

export type ToolChain = ToolCall[];

type ParsedToolCallFor<K extends ToolName> = {
  tool_name: K;
  parameters: ToolParameters<K>;
} & ParsedToolBranch;

export type ParsedToolCall = { [K in ToolName]: ParsedToolCallFor<K> }[ToolName];

export type ParsedToolChain = ParsedToolCall[];

/**
 * Compile-time completeness guards. Trivially true while the unions stay
 * derived from the catalog — they exist to fail the build if a hand-written
 * parallel union is ever reintroduced and drifts from the catalog.
 */
type ExpectNever<T extends never> = T;

type _ToolCallMissesCatalogEntry = ExpectNever<Exclude<ToolName, ToolCall['tool_name']>>;
type _ToolCallExceedsCatalog = ExpectNever<Exclude<ToolCall['tool_name'], ToolName>>;
type _ParsedToolCallMissesCatalogEntry = ExpectNever<
  Exclude<ToolName, ParsedToolCall['tool_name']>
>;
type _ParsedToolCallExceedsCatalog = ExpectNever<Exclude<ParsedToolCall['tool_name'], ToolName>>;

const toolBranchesShape = () => ({
  onSuccess: z.lazy(() => toolChainSchema).optional(),
  onError: z.lazy(() => toolChainSchema).optional(),
});

const toolCallArm = (name: ToolName) =>
  z
    .object({
      tool_name: z.literal(name),
      parameters: TOOL_CATALOG[name].parametersSchema,
      ...toolBranchesShape(),
    })
    .strict();

type DynamicToolCallArm = ReturnType<typeof toolCallArm>;

function buildToolCallArms(
  first: ToolName,
  remaining: readonly ToolName[],
): [DynamicToolCallArm, ...DynamicToolCallArm[]] {
  return [toolCallArm(first), ...remaining.map((name) => toolCallArm(name))];
}

const [firstToolName, ...remainingToolNames] = TOOL_NAMES;
if (firstToolName === undefined) {
  throw new Error('TOOL_CATALOG must declare at least one tool');
}
const toolCallArms = buildToolCallArms(firstToolName, remainingToolNames);

function isParsedToolCall(value: unknown): value is ParsedToolCall {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  if (!('tool_name' in value) || typeof value.tool_name !== 'string') return false;
  if (!isToolName(value.tool_name) || !('parameters' in value)) return false;
  return TOOL_CATALOG[value.tool_name].parametersSchema.safeParse(value.parameters).success;
}

/**
 * The tool-call contract: a strict discriminated union keyed by `tool_name`,
 * generated from `TOOL_CATALOG`. Result branches are limited to `onSuccess` and
 * `onError` on awaited tools; arbitrary imperative fields
 * (`if`/`when`/`cond`/`loop`) are rejected.
 */
export const toolCallSchema: z.ZodType<ParsedToolCall> = z.lazy(() =>
  z
    .discriminatedUnion('tool_name', toolCallArms)
    .superRefine((call, ctx) => {
      if (TOOL_RUN_MODES[call.tool_name] !== 'immediate') return;

      if (call.onSuccess !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['onSuccess'],
          message: 'immediate tools cannot declare onSuccess branches',
        });
      }

      if (call.onError !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['onError'],
          message: 'immediate tools cannot declare onError branches',
        });
      }
    })
    .pipe(z.custom<ParsedToolCall>(isParsedToolCall)),
);

export const toolChainSchema: z.ZodType<ParsedToolChain> = z.array(toolCallSchema);

export function isToolName(value: string): value is ToolName {
  return Object.prototype.hasOwnProperty.call(TOOL_CATALOG, value);
}

export function toolHasImplementation(
  value: string,
  implementation: ToolImplementation,
): value is ToolName {
  return isToolName(value) && TOOL_IMPLEMENTATIONS[value] === implementation;
}

export function toolHasProviderType(
  value: string,
  providerType: ToolProviderType,
): value is ToolName {
  return isToolName(value) && TOOL_PROVIDER_TYPES[value] === providerType;
}

// Identity-class argument names, in snake_case or camelCase. A provider-exposed
// tool must never declare one: user identity is resolved server-side from the
// call's session binding, never from an LLM-authored argument (ADR-045). A
// conversation correlation handle (`conversation_id`) names a provider
// conversation, not a member, and is deliberately outside this class.
const IDENTITY_FIELD_NAME_PATTERN = /^(?:user|member|session|account)_?id$/i;

/**
 * Catalog lint predicate: does `name` declare an identity-class tool argument
 * (`user_id`/`userId`, `member_id`/`memberId`, `session_id`/`sessionId`,
 * `account_id`/`accountId`)? Used by the shared-catalog and provider-config
 * tests to fail any provider-exposed tool schema that carries one.
 */
export function isIdentityFieldName(name: string): boolean {
  return IDENTITY_FIELD_NAME_PATTERN.test(name);
}
